import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCampaignPermissions } from '../../../hooks/useCampaignPermissions';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import {
  applyCampaignAction,
  createCampaign,
  deleteCampaign,
  fetchCampaigns,
  replaceRetryRules,
  updateCampaign,
} from '../../../services/callcenterV2/campaignsService';
import realtimeService from '../../../services/callcenterV2/realtimeService';
import CampaignForm from './CampaignForm';
import '../../../components/CallcenterV2/ccv2-components.css';
import '../config-styles.css';

const STATUS_LABELS = {
  draft: 'Rascunho',
  scheduled: 'Agendada',
  running: 'Discando',
  paused: 'Pausada',
  draining: 'Drenando',
  completed: 'Concluída',
  archived: 'Arquivada',
};

const STATUS_BADGE = {
  running: 'ccv2-badge--success',
  scheduled: 'ccv2-badge--info',
  paused: 'ccv2-badge--warn',
  draining: 'ccv2-badge--warn',
  draft: 'ccv2-badge--muted',
  completed: 'ccv2-badge--info',
  archived: 'ccv2-badge--muted',
};

const ACTIONS_BY_STATUS = {
  draft: ['start'],
  scheduled: ['start', 'stop'],
  running: ['pause', 'stop'],
  paused: ['resume', 'stop'],
  draining: [],
  completed: ['archive'],
  archived: [],
};

const ACTION_LABELS = {
  start: '▶ Iniciar',
  pause: '⏸ Pausar',
  resume: '▶ Retomar',
  stop: '⏹ Parar',
  archive: '📦 Arquivar',
};

const ACTION_CONFIRM = {
  start: 'A campanha começará a originar chamadas assim que houver agentes livres na fila.',
  pause: 'Nenhuma chamada nova será originada. As chamadas em curso continuam.',
  resume: 'A campanha volta a originar chamadas.',
  stop: 'A campanha entra em drenagem: nada novo é discado e os leads em voo terminam.',
  archive: 'A campanha sai da lista operacional. Nenhum dado é apagado.',
};

function Campaigns() {
  const { canManage } = useCampaignPermissions();

  const [campaigns, setCampaigns] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCampaigns(statusFilter ? { status: statusFilter } : {});
      setCampaigns(Array.isArray(data?.items) ? data.items : []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const unsubscribe = realtimeService.subscribeEvent('campaign.', (event) => {
      if (event.type === 'campaign.stats') return;
      load();
    });
    return unsubscribe;
  }, [load]);

  const notify = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleSave = async (payload, retryRules) => {
    setSaving(true);
    try {
      let campaignId;
      if (editing) {
        await updateCampaign(editing.idDialerCampaign, payload);
        campaignId = editing.idDialerCampaign;
        notify('Campanha atualizada.');
      } else {
        const created = await createCampaign(payload);
        campaignId = created?.idDialerCampaign;
        notify('Campanha criada em rascunho.');
      }
      if (campaignId && Array.isArray(retryRules) && retryRules.length > 0) {
        await replaceRetryRules(campaignId, retryRules);
      }
      setShowForm(false);
      setEditing(null);
      setError(null);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const runAction = async () => {
    if (!pendingAction) return;
    setSaving(true);
    try {
      await applyCampaignAction(pendingAction.campaign.idDialerCampaign, pendingAction.action);
      notify(`Ação "${pendingAction.action}" aplicada.`);
      setPendingAction(null);
      setError(null);
      load();
    } catch (e) {
      setError(e.message);
      setPendingAction(null);
    } finally {
      setSaving(false);
    }
  };

  const runDelete = async () => {
    if (!pendingDelete) return;
    setSaving(true);
    try {
      await deleteCampaign(pendingDelete.idDialerCampaign);
      notify('Campanha excluída.');
      setPendingDelete(null);
      setError(null);
      load();
    } catch (e) {
      setError(e.message);
      setPendingDelete(null);
    } finally {
      setSaving(false);
    }
  };

  const rows = useMemo(() => campaigns, [campaigns]);

  return (
    <div className="ccv2-page-container ccv2-p5-container">
      <div className="ccv2-page-header">
        <div className="ccv2-page-title-area">
          <h1>Campanhas</h1>
          <p>Discador outbound: listas, ritmo de discagem e compliance</p>
        </div>
        <div className="ccv2-page-actions">
          <button type="button" className="ccv2-btn ccv2-btn-secondary" onClick={load}>🔄 Atualizar</button>
          <Link className="ccv2-btn ccv2-btn-outline" to="/callcenterV2/campaigns/dnc">🚫 Lista de bloqueio</Link>
          {canManage && (
            <button
              type="button"
              className="ccv2-btn ccv2-btn-primary"
              onClick={() => { setEditing(null); setShowForm(true); }}
            >
              + Nova campanha
            </button>
          )}
        </div>
      </div>

      <div className="ccv2-filters">
        <div className="ccv2-filter-group">
          <label htmlFor="ccv2-campaign-status-filter">Status</label>
          <select
            id="ccv2-campaign-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Todos os status</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {success && <div className="ccv2-p5-success">{success}</div>}
      {error && (
        <div className="ccv2-p5-error">
          ⚠️ {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        </div>
      )}

      {loading ? (
        <div className="ccv2-config-loading"><div className="ccv2-spinner" /></div>
      ) : (
        <div className="ccv2-config-card">
          <div className="ccv2-config-table-wrapper">
            <table className="ccv2-config-table">
              <thead>
                <tr>
                  <th>Campanha</th>
                  <th>Status</th>
                  <th>Modo</th>
                  <th>Fila</th>
                  <th>Tronco</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={6} className="ccv2-empty-row">Nenhuma campanha cadastrada.</td></tr>
                ) : rows.map((campaign) => (
                  <tr key={campaign.idDialerCampaign}>
                    <td className="ccv2-cell-name">
                      <Link to={`/callcenterV2/campaigns/${campaign.idDialerCampaign}`}>{campaign.name}</Link>
                      {campaign.description && (
                        <span className="ccv2-cell-secondary">{campaign.description}</span>
                      )}
                    </td>
                    <td>
                      <span className={`ccv2-badge ${STATUS_BADGE[campaign.status] || 'ccv2-badge--muted'}`}>
                        {STATUS_LABELS[campaign.status] || campaign.status}
                      </span>
                    </td>
                    <td>
                      {campaign.dialMode === 'preview' ? 'Preview' : 'Power'}
                      <span className="ccv2-cell-secondary">ratio {campaign.powerRatio}</span>
                    </td>
                    <td>{campaign.queueName}</td>
                    <td>{campaign.trunkName}</td>
                    <td>
                      <div className="ccv2-cell-actions">
                        {canManage && (ACTIONS_BY_STATUS[campaign.status] || []).map((action) => (
                          <button
                            key={action}
                            type="button"
                            className={`ccv2-btn ccv2-btn-sm ${action === 'stop' ? 'ccv2-btn-danger' : 'ccv2-btn-primary'}`}
                            onClick={() => setPendingAction({ campaign, action })}
                          >
                            {ACTION_LABELS[action]}
                          </button>
                        ))}
                        {canManage && (
                          <button
                            type="button"
                            className="ccv2-btn-icon ccv2-btn-edit"
                            title="Editar campanha"
                            onClick={() => { setEditing(campaign); setShowForm(true); }}
                          >
                            ✏️
                          </button>
                        )}
                        {canManage && ['draft', 'archived'].includes(campaign.status) && (
                          <button
                            type="button"
                            className="ccv2-btn-icon ccv2-btn-delete"
                            title="Excluir campanha"
                            onClick={() => setPendingDelete(campaign)}
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <CampaignForm
          campaign={editing}
          saving={saving}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSubmit={handleSave}
        />
      )}

      <ConfirmModal
        open={Boolean(pendingAction)}
        title={pendingAction ? `${ACTION_LABELS[pendingAction.action]} — ${pendingAction.campaign.name}` : ''}
        body={pendingAction ? ACTION_CONFIRM[pendingAction.action] : ''}
        confirmLabel="Confirmar"
        danger={pendingAction?.action === 'stop'}
        loading={saving}
        onCancel={() => setPendingAction(null)}
        onConfirm={runAction}
      />

      <ConfirmModal
        open={Boolean(pendingDelete)}
        title={pendingDelete ? `Excluir ${pendingDelete.name}?` : ''}
        body="A campanha e seus leads serão removidos definitivamente."
        confirmLabel="Excluir"
        danger
        loading={saving}
        onCancel={() => setPendingDelete(null)}
        onConfirm={runDelete}
      />
    </div>
  );
}

export default Campaigns;
